/**
 * @fileoverview �`���[�g���A���̍ŏ��I�[�P�X�g���[�^�[�i�n�C���C�g + ���͐���j
 */
import gameState from '../../core/game-state.js';
import { resume as resumeLimit } from '../../gameplay/limit-system/limit-suspension.js';
import { STATE_PATHS } from '../../shared/constants/state-paths.js';
import { MinimalErrorTracker } from '../../shared/log.js';

import {
  updateGuardState,
  registerActionCallbacks,
  setSwapConstraints,
} from './tutorial-guards.js';
import {
  ensureMaskRoot,
  ensureCardRoot,
  attachPointerGuards,
  detachPointerGuards,
  attachRepositionListeners,
  detachRepositionListeners,
  setAllowedSelectors,
  clearOverlay,
  setOverlayDim,
} from './tutorial-overlay.js';
import {
  isTutorialActive,
  setTutorialActive,
  isPromptScheduled,
  setPromptScheduled,
  resetSessionState,
} from './tutorial-session-state.js';
import { initializeStatusWatcher, teardownStatusWatcher } from './tutorial-status-watcher.js';
import {
  renderPrompt,
  renderCompletionCard,
  advanceToNextStep,
  handleStartAction as engineHandleStartAction,
  handleBoardMatchAction as engineHandleBoardMatchAction,
  setTutorialState,
  resetStepProgress,
  LIMIT_SUSPEND_REASON,
} from './tutorial-step-engine.js';
import {
  getOnboardingStatus,
  markCompleted,
  markSkipped,
  resetOnboarding,
  saveOnboardingStatus,
} from './tutorial-storage.js';

function consumeAdvancePromise(promise) {
  if (!promise || typeof promise.then !== 'function') {
    return;
  }
  promise
    .then((result) => {
      if (result === null) {
        completeTutorial();
        return;
      }
      if (result && typeof result === 'object' && result.completed) {
        completeTutorial();
      }
    })
    .catch((error) => {
      MinimalErrorTracker.warn(
        'TUTORIAL',
        `Failed to advance tutorial: ${error?.message ?? error}`,
      );
    });
}

function schedulePromptRender() {
  if (isPromptScheduled()) {
    return;
  }
  setPromptScheduled(true);
  Promise.resolve()
    .then(() => {
      setPromptScheduled(false);
      ensureMaskRoot();
      ensureCardRoot();
      attachPointerGuards();
      setAllowedSelectors([]);
      setSwapConstraints(null);
      if (!isTutorialActive()) {
        setTutorialActive(true);
      }
      setTutorialState('idle', null, []);
      renderPrompt({ onSkip: skipTutorial, onStart: beginTutorial });
    })
    .catch((error) => {
      MinimalErrorTracker.warn(
        'TUTORIAL',
        `Failed to schedule tutorial prompt: ${error?.message ?? error}`,
      );
    });
}

function completeTutorial() {
  setSwapConstraints(null);
  markCompleted();
  renderCompletionCard(finalize);
}

function skipTutorial() {
  setTutorialState('skipped', null, []);
  markSkipped();
  finalize();
}

function beginTutorial() {
  saveOnboardingStatus({ status: 'running', completedAt: null }, 'tutorial_onboarding_running');
  setOverlayDim(false);
  attachPointerGuards();
  attachRepositionListeners();
  setTutorialActive(true);
  resetStepProgress();
  setSwapConstraints(null);
  consumeAdvancePromise(advanceToNextStep());
}

function finalize() {
  clearOverlay();
  detachPointerGuards();
  detachRepositionListeners();
  setAllowedSelectors([]);
  setSwapConstraints(null);
  resumeLimit(LIMIT_SUSPEND_REASON);
  resetSessionState();
}

function cleanupOnTeardown() {
  finalize();
  teardownStatusWatcher();
  const status = gameState.getState(STATE_PATHS.UI_TUTORIAL_STATUS) ?? 'idle';
  const step = gameState.getState(STATE_PATHS.UI_TUTORIAL_STEP) ?? null;
  updateGuardState({ status, step, allowedActions: [] });
  registerActionCallbacks({
    start: null,
    boardMatch: null,
  });
}

function handleStartAction(payload) {
  const result = engineHandleStartAction(payload);
  consumeAdvancePromise(result);
  return result;
}

function handleBoardMatchAction(payload) {
  const result = engineHandleBoardMatchAction(payload);
  consumeAdvancePromise(result);
  return result;
}

export async function initTutorial() {
  try {
    registerActionCallbacks({
      start: handleStartAction,
      boardMatch: handleBoardMatchAction,
    });
    initializeStatusWatcher(schedulePromptRender);
    const onboarding = getOnboardingStatus();

    if (onboarding.status === 'completed') {
      setTutorialState('completed', null, []);
      return () => cleanupOnTeardown();
    }

    if (onboarding.status === 'skipped') {
      setTutorialState('skipped', null, []);
      return () => cleanupOnTeardown();
    }

    resetStepProgress();
    setTutorialState('idle', null, []);
    schedulePromptRender();

    return () => cleanupOnTeardown();
  } catch (error) {
    MinimalErrorTracker.warn(
      'TUTORIAL',
      `Tutorial initialization failed: ${error?.message ?? error}`,
    );
    setTutorialState('idle', null, []);
    return () => cleanupOnTeardown();
  }
}

export function replayTutorial() {
  resetOnboarding();
  if (!isTutorialActive()) {
    setTutorialActive(true);
  }
  ensureMaskRoot();
  ensureCardRoot();
  attachPointerGuards();
  setAllowedSelectors([]);
  setSwapConstraints(null);
  resetStepProgress();
  renderPrompt({ onSkip: skipTutorial, onStart: beginTutorial });
}
